import { useState } from "react";
import { QualityInspection } from "@/data/mockData";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Clock,
  TrendingUp,
  TrendingDown,
  BarChart3,
  PieChart,
  Activity,
  FileText,
  Calendar,
  Filter
} from "lucide-react";

interface QualityDashboardProps {
  inspections: QualityInspection[];
  isLoading?: boolean;
}

export const QualityDashboard = ({ inspections = [], isLoading = false }: QualityDashboardProps) => {
  const [timeFilter, setTimeFilter] = useState("30d");

  // Calculate metrics
  const totalInspections = inspections.length;
  const passedInspections = inspections.filter(i => i.overallStatus === "Passed").length;
  const failedInspections = inspections.filter(i => i.overallStatus === "Failed").length;
  const pendingInspections = inspections.filter(i => i.overallStatus === "Pending").length;
  const inProgressInspections = inspections.filter(i => i.overallStatus === "In Progress").length;

  const passRate = totalInspections > 0 ? Math.round((passedInspections / totalInspections) * 100) : 0;

  // Calculate defect metrics - using rejection reasons as defects
  const allDefects = (inspections ?? []).flatMap(i => 
    (i.items ?? []).flatMap(item => 
      (item.rejectionReasons ?? []).map(reason => ({
        reason,
        severity: reason.includes('Critical') || reason.includes('Failure') ? 'Critical' : 
                 reason.includes('Major') || reason.includes('damage') ? 'Major' : 'Minor'
      }))
    )
  );
  const criticalDefects = allDefects.filter(d => d.severity === "Critical").length;
  const majorDefects = allDefects.filter(d => d.severity === "Major").length;
  const minorDefects = allDefects.filter(d => d.severity === "Minor").length;

  // Calculate vendor performance
  const vendorPerformance = (inspections ?? []).reduce((acc, inspection) => {
    const poNumber = `PO-${inspection.poId}`;
    if (!acc[poNumber]) {
      acc[poNumber] = {
        poNumber,
        totalInspections: 0,
        passedInspections: 0,
        totalItems: 0,
        passedItems: 0,
        defects: 0
      };
    }
    
    acc[poNumber].totalInspections++;
    if (inspection.overallStatus === "Passed") {
      acc[poNumber].passedInspections++;
    }
    
    const items = inspection.items ?? [];
    acc[poNumber].totalItems += items.length;
    acc[poNumber].passedItems += items.filter(item => item.qualityRating >= 4.0).length;
    acc[poNumber].defects += items.reduce((sum, item) => sum + (item.rejectionReasons?.length ?? 0), 0);
    
    return acc;
  }, {} as Record<string, any>);

  const vendorPerformanceArray = Object.values(vendorPerformance).map((vendor: any) => ({
    ...vendor,
    passRate: vendor.totalItems > 0 ? Math.round((vendor.passedItems / vendor.totalItems) * 100) : 0,
    defectRate: vendor.totalItems > 0 ? Math.round((vendor.defects / vendor.totalItems) * 100) : 0
  }));

  // Recent inspections
  const recentInspections = (inspections ?? [])
    .sort((a, b) => new Date(b.inspectionDate).getTime() - new Date(a.inspectionDate).getTime())
    .slice(0, 5);

  // Quality trends (mock data for now)
  const qualityTrends = [
    { month: "Jan", passRate: 85, inspections: 12 },
    { month: "Feb", passRate: 88, inspections: 15 },
    { month: "Mar", passRate: 82, inspections: 18 },
    { month: "Apr", passRate: 90, inspections: 14 },
    { month: "May", passRate: 87, inspections: 16 },
    { month: "Jun", passRate: 92, inspections: 20 }
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "Passed":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "Failed":
        return <XCircle className="h-4 w-4 text-red-500" />;
      case "Pending":
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case "In Progress":
        return <AlertTriangle className="h-4 w-4 text-blue-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case "Passed":
        return "default";
      case "Failed":
        return "destructive";
      case "Pending":
        return "secondary";
      case "In Progress":
        return "outline";
      default:
        return "outline";
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-muted rounded w-1/2 mb-2"></div>
                <div className="h-8 bg-muted rounded w-1/3"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Quality Control Dashboard</h2>
          <p className="text-muted-foreground">Monitor quality inspection performance and trends</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value)}
            className="px-3 py-2 border border-input bg-background rounded-md text-sm"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4 mr-2" />
            Filter
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pass Rate</p>
                <p className="text-2xl font-bold">{passRate}%</p>
                <p className="text-xs text-green-600">
                  {passedInspections} of {totalInspections} inspections
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Inspections</p>
                <p className="text-2xl font-bold">{totalInspections}</p>
                <p className="text-xs text-muted-foreground">
                  {inProgressInspections} in progress
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-lg flex items-center justify-center">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Failed Inspections</p>
                <p className="text-2xl font-bold">{failedInspections}</p>
                <p className="text-xs text-red-600">
                  {totalInspections > 0 ? Math.round((failedInspections / totalInspections) * 100) : 0}% failure rate
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Defects</p>
                <p className="text-2xl font-bold">{allDefects.length}</p>
                <p className="text-xs text-orange-600">
                  {criticalDefects} critical, {majorDefects} major
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="trends">Trends</TabsTrigger>
          <TabsTrigger value="vendors">Vendor Performance</TabsTrigger>
          <TabsTrigger value="recent">Recent Activity</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Inspection Status Distribution</CardTitle>
                <CardDescription>Current status of all quality inspections</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getStatusIcon("Passed")}
                      <span className="text-sm">Passed</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{passedInspections}</span>
                      <div className="w-20 bg-muted rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full" 
                          style={{ width: `${totalInspections > 0 ? (passedInspections / totalInspections) * 100 : 0}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getStatusIcon("Failed")}
                      <span className="text-sm">Failed</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{failedInspections}</span>
                      <div className="w-20 bg-muted rounded-full h-2">
                        <div 
                          className="bg-red-500 h-2 rounded-full" 
                          style={{ width: `${totalInspections > 0 ? (failedInspections / totalInspections) * 100 : 0}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getStatusIcon("In Progress")}
                      <span className="text-sm">In Progress</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{inProgressInspections}</span>
                      <div className="w-20 bg-muted rounded-full h-2">
                        <div 
                          className="bg-blue-500 h-2 rounded-full" 
                          style={{ width: `${totalInspections > 0 ? (inProgressInspections / totalInspections) * 100 : 0}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getStatusIcon("Pending")}
                      <span className="text-sm">Pending</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{pendingInspections}</span>
                      <div className="w-20 bg-muted rounded-full h-2">
                        <div 
                          className="bg-yellow-500 h-2 rounded-full" 
                          style={{ width: `${totalInspections > 0 ? (pendingInspections / totalInspections) * 100 : 0}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Defect Severity Breakdown</CardTitle>
                <CardDescription>Distribution of defects by severity level</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <span className="text-sm">Critical</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{criticalDefects}</span>
                      <div className="w-20 bg-muted rounded-full h-2">
                        <div 
                          className="bg-red-500 h-2 rounded-full" 
                          style={{ width: `${allDefects.length > 0 ? (criticalDefects / allDefects.length) * 100 : 0}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                      <span className="text-sm">Major</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{majorDefects}</span>
                      <div className="w-20 bg-muted rounded-full h-2">
                        <div 
                          className="bg-orange-500 h-2 rounded-full" 
                          style={{ width: `${allDefects.length > 0 ? (majorDefects / allDefects.length) * 100 : 0}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                      <span className="text-sm">Minor</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{minorDefects}</span>
                      <div className="w-20 bg-muted rounded-full h-2">
                        <div 
                          className="bg-yellow-500 h-2 rounded-full" 
                          style={{ width: `${allDefects.length > 0 ? (minorDefects / allDefects.length) * 100 : 0}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Quality Trends</CardTitle>
              <CardDescription>Pass rate and inspection volume over time</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Quality trends chart would be displayed here</p>
                  <p className="text-sm">Integration with charting library needed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vendor Performance Tab */}
        <TabsContent value="vendors" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Vendor Performance</CardTitle>
              <CardDescription>Quality performance by vendor/PO</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {vendorPerformanceArray.length > 0 ? (
                  vendorPerformanceArray.map((vendor: any) => (
                    <div key={vendor.poNumber} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium">PO: {vendor.poNumber}</h4>
                        <Badge variant={vendor.passRate >= 90 ? "default" : vendor.passRate >= 70 ? "secondary" : "destructive"}>
                          {vendor.passRate}% Pass Rate
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Inspections</p>
                          <p className="font-medium">{vendor.totalInspections}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Items Tested</p>
                          <p className="font-medium">{vendor.totalItems}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Items Passed</p>
                          <p className="font-medium text-green-600">{vendor.passedItems}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Defects</p>
                          <p className="font-medium text-red-600">{vendor.defects}</p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No vendor performance data available</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Recent Activity Tab */}
        <TabsContent value="recent" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recent Inspections</CardTitle>
              <CardDescription>Latest quality inspection activities</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recentInspections.length > 0 ? (
                  recentInspections.map((inspection) => (
                    <div key={inspection.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        {getStatusIcon(inspection.overallStatus)}
                        <div>
                          <p className="font-medium text-sm">INSP-{inspection.id}</p>
                          <p className="text-xs text-muted-foreground">
                            PO-{inspection.poId} • Inspector {inspection.inspectorId}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant={getStatusVariant(inspection.overallStatus) as any} className="text-xs">
                          {inspection.overallStatus}
                        </Badge>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(inspection.inspectionDate).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No recent inspections found</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
